import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import toast from 'react-hot-toast';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [roleAccounts, setRoleAccounts] = useState([]);
  const [quickRole, setQuickRole] = useState(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/auth/roles')
      .then(res => setRoleAccounts(res.data.accounts || []))
      .catch(() => setRoleAccounts([]));
  }, []);

  const quickLogin = async (acct) => {
    setQuickRole(acct.label);
    try {
      await login(acct.email, acct.password);
      toast.success(`Signed in as ${acct.label}`);
      await new Promise(r => setTimeout(r, 100));
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed');
    }
    setQuickRole(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast.success('Welcome back!');
        await new Promise(r => setTimeout(r, 100));
        navigate('/', { replace: true });
      } else {
        await register({ ...form });
        toast.success('Account created!');
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  const u = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" data-testid="auth-page">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: '#0C2417' }}>
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full" style={{ background: '#12301F' }} />
        <div className="absolute top-24 right-28 w-28 h-28 rounded-full blur-2xl opacity-50" style={{ background: '#FAAB1C' }} />
        <div className="absolute top-28 right-32 w-16 h-16 rounded-full" style={{ background: '#FAAB1C' }} />
        <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: 'linear-gradient(90deg,#FAAB1C 62%,#21A84A 62%)' }} />
        <img src="/samaritan-logo.png" alt="Samaritan Solar" className="h-10 w-fit relative" />
        <div className="relative max-w-lg">
          <p className="text-[11px] font-bold tracking-[0.25em]" style={{ color: '#FAAB1C' }}>OPERATIONS SUITE</p>
          <h1 className="font-heading font-black text-5xl leading-[1.02] mt-4">Clean power.<br /><span style={{ color: '#FAAB1C' }}>Honest</span> people.</h1>
          <p className="text-slate-400 mt-6 text-base leading-relaxed">Lead intelligence, shock-and-awe proposals, AI rooftop design, hiring and marketing — the whole Samaritan Solar operation in one command center.</p>
          <div className="grid grid-cols-3 gap-3 mt-10">
            {[['20+', 'years in solar'], ['NY · NJ · CT · PA', 'service area'], ['EPC', 'design → install → service']].map(([v, l]) => (
              <div key={l} className="rounded-xl p-4" style={{ background: '#12301F' }}><p className="font-heading font-black text-xl">{v}</p><p className="text-[10px] font-bold tracking-widest mt-1" style={{ color: '#21A84A' }}>{l.toUpperCase()}</p></div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-500">samaritansolar.com · Two decades of industry-leading experience</p>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md relative z-10">
        <div className="mb-8">
          <img src="/samaritan-logo.png" alt="Samaritan Solar" className="h-9 lg:hidden mb-6" />
          <p className="text-[10px] font-bold tracking-[0.2em]" style={{ color: '#FAAB1C' }}>{mode === 'login' ? 'WELCOME BACK' : 'JOIN THE TEAM'}</p>
          <h2 className="font-heading text-3xl font-black text-text-primary tracking-tight mt-1">{mode === 'login' ? 'Sign in to Samaritan' : 'Create your account'}</h2>
          <p className="text-text-muted text-sm mt-1">Reps, designers, installers and managers — one login.</p>
        </div>

        <div className="bg-surface border border-border rounded-2xl shadow-card p-8 animate-scale-in">
          <>
            <div className="flex bg-surface-alt rounded-xl p-1 mb-6">
              {['login', 'register'].map(m => (
                <button key={m} onClick={() => setMode(m)} className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all capitalize ${mode === m ? 'bg-surface text-primary shadow-card' : 'text-text-muted hover:text-text-secondary'}`} data-testid={`${m}-tab`}>{m === 'login' ? 'Sign In' : 'Sign Up'}</button>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {mode === 'register' && (
                <>
                  <input type="text" required placeholder="Full Name" value={form.name} onChange={e => u('name', e.target.value)} className="input-field" data-testid="name-input" />
                  <input type="text" placeholder="Company (optional)" value={form.company} onChange={e => u('company', e.target.value)} className="input-field" data-testid="company-input" />
                  <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={e => u('phone', e.target.value)} className="input-field" data-testid="phone-input" />
                </>
              )}
              <input type="email" required placeholder="Email Address" value={form.email} onChange={e => u('email', e.target.value)} className="input-field" data-testid="email-input" />
              <input type="password" required placeholder="Password" value={form.password} onChange={e => u('password', e.target.value)} className="input-field" data-testid="password-input" />
              <button type="submit" disabled={loading} className="btn-primary w-full mt-2" data-testid="auth-submit-btn">
                {loading && <i className="fa-solid fa-spinner fa-spin mr-2" />}
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>
          </>
        </div>

        {mode === 'login' && roleAccounts.length > 0 && (
          <div className="mt-6" data-testid="role-logins">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px flex-1 bg-border" />
              <p className="text-[10px] font-bold tracking-[0.2em] text-text-muted">SIGN IN BY ROLE</p>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="space-y-2">
              {roleAccounts.map(a => (
                <button
                  key={a.email}
                  onClick={() => quickLogin(a)}
                  disabled={!!quickRole}
                  className="w-full text-left flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 hover:border-primary hover:shadow-card transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 disabled:opacity-50"
                  data-testid={`role-login-${a.label.toLowerCase()}`}
                >
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#FAAB1C1A', color: '#B87708' }}>
                    <i className={`fa-solid ${a.icon}`} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-bold text-text-primary">{a.label}</span>
                      {a.is_super && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#21A84A1A', color: '#158239' }}>FULL ACCESS</span>}
                    </span>
                    <span className="block text-xs text-text-muted truncate">{a.blurb}</span>
                  </span>
                  {quickRole === a.label
                    ? <i className="fa-solid fa-spinner fa-spin text-text-muted" />
                    : <i className="fa-solid fa-arrow-right text-text-muted text-xs" />}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-text-muted mt-3">
              Or use the form above with any team credentials. Role accounts are for evaluation and can be turned off with <span className="font-mono">DEMO_LOGINS_ENABLED=false</span>.
            </p>
          </div>
        )}

        <p className="text-center text-xs text-text-muted mt-6">
          {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-primary font-semibold hover:underline" data-testid="toggle-auth-mode">{mode === 'login' ? 'Sign up' : 'Sign in'}</button>
        </p>
      </div>
      </div>
    </div>
  );
}
